/**
 * Memory management utilities for browser optimization
 */

// Check if performance memory API is available
const getMemoryInfo = (): any => {
  try {
    // @ts-ignore - experimental API
    return (performance as any).memory || null;
  } catch (error) {
    return null;
  }
};

// Monitor memory usage and warn if high
export const checkMemoryUsage = (componentName: string = 'Unknown'): void => {
  try {
    const memInfo = getMemoryInfo();
    if (memInfo) {
      const usedMB = Math.round(memInfo.usedJSHeapSize / 1024 / 1024);
      const totalMB = Math.round(memInfo.totalJSHeapSize / 1024 / 1024);
      const limitMB = Math.round(memInfo.jsHeapSizeLimit / 1024 / 1024);
      
      console.log(`${componentName} - Memory: ${usedMB}MB used, ${totalMB}MB total, ${limitMB}MB limit`);
      
      // Warn if memory usage is high
      if (usedMB > 150) { // More than 150MB
        console.warn(`⚠️ High memory usage detected in ${componentName}: ${usedMB}MB`);
        
        // Force garbage collection if possible
        if (window.gc) {
          console.log('Attempting garbage collection...');
          window.gc();
        }
      }
      
      // Critical memory warning
      if (usedMB > 300) { // More than 300MB
        console.error(`🚨 Critical memory usage in ${componentName}: ${usedMB}MB! Consider refreshing the page.`);
      }
    }
  } catch (error) {
    console.warn('Could not check memory usage:', error);
  }
};

// Clean up localStorage/sessionStorage if too large
export const cleanupBrowserStorage = (): void => {
  try {
    // Check localStorage size
    const localStorageSize = JSON.stringify(localStorage).length;
    if (localStorageSize > 5 * 1024 * 1024) { // More than 5MB
      console.warn('localStorage is large, clearing non-essential data');
      // Keep only essential keys (add your app's essential keys here)
      const essentialKeys = ['theme', 'language', 'userPreferences'];
      const backup: Record<string, string> = {};
      
      essentialKeys.forEach(key => {
        const value = localStorage.getItem(key);
        if (value) backup[key] = value;
      });
      
      localStorage.clear();
      
      Object.entries(backup).forEach(([key, value]) => {
        localStorage.setItem(key, value);
      });
      
      console.log('localStorage cleaned up');
    }
    
    // Check sessionStorage size
    const sessionStorageSize = JSON.stringify(sessionStorage).length;
    if (sessionStorageSize > 10 * 1024 * 1024) { // More than 10MB
      console.warn('sessionStorage is extremely large, consider clearing it');
    }
  } catch (error) {
    console.error('Storage cleanup failed:', error);
    // If JSON.stringify fails, storage might be corrupted
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch (clearError) {
      console.error('Could not clear storage:', clearError);
    }
  }
};

// iOS-specific memory optimization
export const optimizeForIOS = (): void => {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  
  if (isIOS) {
    console.log('Applying iOS-specific optimizations...');
    
    // Reduce image quality on iOS
    const images = document.querySelectorAll('img[loading="lazy"]');
    images.forEach(img => {
      // Add intersection observer for better iOS performance
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const target = entry.target as HTMLImageElement;
              // Ensure images are properly optimized for iOS
              if (!target.hasAttribute('data-optimized')) {
                target.style.willChange = 'auto';
                target.setAttribute('data-optimized', 'true');
                observer.unobserve(target);
              }
            }
          });
        });
        observer.observe(img);
      }
    });
    
    // Force cleanup after navigation on iOS
    window.addEventListener('pageshow', () => {
      setTimeout(() => {
        checkMemoryUsage('iOS PageShow');
        cleanupBrowserStorage();
      }, 1000);
    });
  }
};

// Initialize memory monitoring
export const initMemoryMonitoring = (): void => {
  // Check memory every 30 seconds
  setInterval(() => {
    checkMemoryUsage('Background Monitor');
  }, 30000);
  
  // Check on page visibility change
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      checkMemoryUsage('Visibility Change');
    }
  });
  
  // iOS optimizations
  optimizeForIOS();
  
  // Initial storage cleanup
  cleanupBrowserStorage();
  
  console.log('Memory monitoring initialized');
};

// Hook for React components
export const useMemoryOptimization = (componentName: string) => {
  const checkMemory = () => checkMemoryUsage(componentName);
  
  return {
    checkMemory,
    cleanupStorage: cleanupBrowserStorage
  };
};